import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "@/components/base/Seo";
import ComicCard from "@/components/feature/ComicCard";
import { comics, categories } from "@/mocks/comics";
import { faqs } from "@/mocks/faq";

const rankTop = comics.filter((c) => c.isHot).sort((a, b) => b.rating - a.rating).slice(0, 5);
const hotRank = rankTop[0];
const hotList = rankTop.slice(1);

const updates = comics.filter((c) => c.isNew || c.updatedAt === "今日更新").slice(0, 6);
const completed = comics.filter((c) => c.status === "已完结").slice(0, 4);

const features = [
  { icon: "ri-calendar-check-line", title: "每日准时更新", desc: "热门榜单与今日更新每天同步，热门新作第一时间上架，追更不迷路。" },
  { icon: "ri-box-3-line", title: "五大题材聚合", desc: "异世界、穿越、奇幻、脑洞、玄幻一站式收录，细分标签帮你精准入坑。" },
  { icon: "ri-star-line", title: "真实口碑评分", desc: "站内读者多维评分，结合阅读热度与人气增速，好作品不会被埋没。" },
  { icon: "ri-book-read-line", title: "完本放心追", desc: "完本推荐栏目收录高评分完结作品，一次追个痛快，告别等更焦虑。" },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-background-800 bg-background-900">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-4 text-left md:px-5"
      >
        <span className="text-sm font-medium text-foreground-950 md:text-base">{q}</span>
        <i className={`shrink-0 text-lg text-primary-400 transition-transform ${open ? "rotate-45" : ""} ri-add-line`} />
      </button>
      {open && <p className="border-t border-background-800 px-4 py-4 text-sm leading-6 text-foreground-500 md:px-5">{a}</p>}
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(q.trim() ? `/category/all?q=${encodeURIComponent(q.trim())}` : "/category/all");
  };

  return (
    <div>
      <Seo
        title="虚空漫画网 | 异世界/穿越/奇幻/脑洞/玄幻漫画合集，热门榜单每日更新"
        description="虚空漫画网聚合海量异世界、穿越、奇幻、脑洞、玄幻题材漫画，每日更新热门榜单与完本推荐。书荒时来这找下一部入坑作。"
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "虚空漫画网",
            url: "https://talespots.github.io",
            description: "异世界、穿越、奇幻、脑洞、玄幻漫画聚合站，每日更新热门榜单与完本推荐。",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://talespots.github.io/category/all?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "虚空漫画网热门榜单",
            description: "每日更新的异世界、穿越、奇幻、脑洞、玄幻漫画热门榜单。",
            url: "https://talespots.github.io",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.slice(0, 4).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      {/* Hero */}
      <section className="relative flex min-h-[600px] w-full items-center justify-center overflow-hidden md:min-h-[640px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=immersive%20dark%20fantasy%20digital%20art%20background%20of%20a%20void%20cosmos%20filled%20with%20warm%20amber%20and%20crimson%20nebula%20clouds%2C%20glowing%20golden%20energy%20streaks%20and%20floating%20ember%20particles%2C%20silhouetted%20fantasy%20castle%20ruins%20on%20the%20horizon%2C%20dramatic%20cinematic%20lighting%2C%20no%20text%2C%20rich%20warm%20color%20gradient%20with%20deep%20shadows&width=1600&height=900&seq=hero-void-2026&orientation=landscape"
            alt="虚空漫画网异世界奇幻漫画背景"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/80 via-background-950/55 to-background-950" />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-20 text-center md:px-6">
          <span className="mx-auto inline-flex items-center gap-2 rounded-full border border-primary-500/40 bg-background-950/50 px-4 py-1.5 text-xs font-medium text-primary-300 backdrop-blur">
            <i className="ri-fire-fill text-sm" />
            异世界 · 穿越 · 奇幻 · 脑洞 · 玄幻
          </span>
          <h1 className="mt-6 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl md:leading-tight">
            让每一部<span className="text-gradient-gold">热血幻想</span>
            <br className="hidden md:block" /> 都有处安放
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-foreground-600 md:text-lg">
            聚合海量异世界、穿越、奇幻、脑洞、玄幻漫画，热门榜单每日更新，完本推荐一网打尽。书荒时，来这里找下一部入坑作。
          </p>

          <form onSubmit={onSearch} className="mx-auto mt-8 flex max-w-xl flex-col gap-2 sm:flex-row sm:gap-0 sm:rounded-full sm:border sm:border-background-700 sm:bg-background-950/70 sm:p-1.5 sm:backdrop-blur">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="搜漫画，如：剑与龙息、玄幻"
                aria-label="搜索漫画"
                className="w-full rounded-full bg-background-950/70 py-3 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none sm:rounded-full sm:bg-transparent"
              />
            </div>
            <button type="submit" className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600">
              立即搜索
            </button>
          </form>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {[
              { num: "2800+", label: "收录作品" },
              { num: "5", label: "题材分类" },
              { num: "300+", label: "每日更新" },
              { num: "9.2", label: "平均评分" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="font-heading text-2xl font-black text-primary-400">{s.num}</p>
                <p className="mt-1 text-xs text-foreground-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 热门榜单 */}
      <section id="rank" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-400">今日热门</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">热门榜单</h2>
            <p className="mt-3 max-w-md text-sm text-foreground-500">综合阅读热度、评分与近七日人气，每日刷新的人气排名。</p>
          </div>
          <Link to="/category/all" className="group flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-400 hover:text-primary-300">
            查看全部
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Link
            to={`/comic/${hotRank.id}`}
            className="group relative col-span-1 cursor-pointer overflow-hidden rounded-2xl border border-primary-500/30 bg-background-900 lg:col-span-2 lg:row-span-2"
          >
            <div className="relative h-72 w-full md:h-[460px]">
              <img src={hotRank.cover} alt={`${hotRank.title}漫画封面`} className="h-full w-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-transparent" />
              <span className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-xl bg-accent-500 font-heading text-xl font-black text-background-950">
                1
              </span>
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-500 px-2 py-1 text-[11px] font-medium text-background-950">{hotRank.categoryName}</span>
                  <span className="rounded-md bg-background-950/70 px-2 py-1 text-[11px] text-foreground-500">热度 {hotRank.views / 10000}万</span>
                </div>
                <h3 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">{hotRank.title}</h3>
                <p className="mt-2 line-clamp-2 max-w-xl text-sm leading-6 text-foreground-600">{hotRank.longDescription}</p>
                <span className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-950 transition-colors group-hover:bg-primary-600">
                  开始追更 <i className="ri-arrow-right-line" />
                </span>
              </div>
            </div>
          </Link>

          <div className="col-span-1 flex flex-col gap-3">
            {hotList.map((c, i) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-xl border border-background-800 bg-background-900 p-3 transition-colors hover:border-primary-500/40"
              >
                <span className={`font-heading text-2xl font-black ${i < 2 ? "text-accent-500" : "text-foreground-600"}`}>{i + 2}</span>
                <img src={c.cover} alt={`${c.title}封面`} className="h-14 w-10 shrink-0 rounded-md object-cover object-top" />
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-300">{c.title}</h3>
                  <p className="mt-0.5 truncate text-xs text-foreground-500">{c.latestChapter}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-0.5 text-primary-400"><i className="ri-star-fill text-[10px]" />{c.rating}</span>
                    <span className="text-foreground-500">{c.views / 10000}万热度</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 分类导航 */}
      <section className="border-y border-background-800 bg-background-900/60">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-400">题材总览</p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">按你的口味入坑</h2>
            </div>
            <p className="max-w-md text-sm text-foreground-500">五大核心题材细分覆盖，点击进入分类，精准找到属于你的那部。</p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {categories.map((c) => {
              const count = comics.filter((x) => x.category === c.slug).length;
              return (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className="group relative cursor-pointer overflow-hidden rounded-2xl border border-background-800"
                >
                  <div className="relative aspect-[4/5] w-full">
                    <img src={c.image} alt={`${c.name}漫画分类`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/35 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-4">
                      <h3 className="font-heading text-lg font-black text-foreground-950">{c.name}</h3>
                      <p className="mt-1 line-clamp-2 text-xs leading-5 text-foreground-500">{c.desc}</p>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-400">
                        {count} 部精选 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* 今日更新 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-400">追更不停</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">今日更新</h2>
            <p className="mt-3 max-w-md text-sm text-foreground-500">最新章节热腾腾上架，第一时间追到最新章节。</p>
          </div>
          <span className="flex items-center gap-1.5 rounded-full border border-accent-500/40 bg-accent-500/10 px-3 py-1.5 text-xs font-medium text-accent-400">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent-500" />
            今日已更新 {updates.length} 部
          </span>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {updates.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* 完本推荐 */}
      <section id="wanben" className="border-y border-background-800 bg-background-900/60">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-400">一次追个痛快</p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">完本推荐</h2>
              <p className="mt-3 max-w-md text-sm text-foreground-500">高分完结佳作，无需等待，一口气读完整个世界的精彩。</p>
            </div>
            <Link to="/category/all?status=finished" className="group flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-400 hover:text-primary-300">
              更多完本
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {completed.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>

      {/* 口碑/为什么选择 */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-400">为什么选虚空</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              书荒终结者，<br className="hidden md:block" /> 为每一个幻想读者而建
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-500 md:text-base">
              我们不是漫无目的的收录站，而是围绕异世界、穿越、奇幻、脑洞、玄幻五大题材，用心筛选、持续更新的幻想漫画聚合地。从热门榜单到完本推荐，让好作品不再被埋没。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/category/all" className="cursor-pointer whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600">
                浏览全部漫画
              </Link>
              <Link to="/about" className="cursor-pointer whitespace-nowrap rounded-lg border border-background-700 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-300">
                了解本站
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {features.map((f) => (
              <div key={f.title} className="rounded-xl border border-background-800 bg-background-900 p-5 transition-colors hover:border-primary-500/40">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500/15 text-xl text-primary-400">
                  <i className={f.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{f.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ 预览 */}
      <section className="border-t border-background-800 bg-background-900/60">
        <div className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-400">常见问题</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-4xl">追更前先看看</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-500">关于收录、榜单、评分与追更的常见疑问，快速解答。</p>
          </div>
          <div className="mt-8 space-y-3">
            {faqs.slice(0, 4).map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/faq" className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-400 hover:text-primary-300">
              查看全部常见问题 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="relative overflow-hidden rounded-3xl border border-primary-500/30 bg-gradient-to-br from-background-900 via-background-900 to-primary-500/10 px-6 py-12 text-center md:px-12 md:py-16">
          <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-accent-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-primary-500/20 blur-3xl" />
          <h2 className="relative font-heading text-2xl font-black text-foreground-950 md:text-4xl">下一部入坑作，正在等你</h2>
          <p className="relative mx-auto mt-4 max-w-xl text-sm text-foreground-500 md:text-base">
            从热门榜单到完本推荐，翻开任意一本，开启一场全新的幻想冒险。书荒不存在的。
          </p>
          <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/category/all" className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-600">
              去挑一部漫画
            </Link>
            <Link to="/faq" className="cursor-pointer whitespace-nowrap rounded-full border border-background-700 px-7 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-500/50 hover:text-primary-300">
              有问题？看这里
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}